<script>
import { mapGetters, mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';
import LoadingState from '../../../../components/widgets/LoadingState.vue';
import CreateStaffModal from './createStaffModal.vue';
import CreateServiceModal from './createServiceModal.vue';
import CreateScheduleModal from './createScheduleModal.vue';
import ViewWorkingHours from './viewWorkingHours.vue';
import ToggleScheduleButton from './toggleScheduleButton.vue';
import ViewStaffServices from './viewStaffServices.vue';
import SettingIntroBanner from 'dashboard/components/widgets/SettingIntroBanner.vue';
import ViewOverrides from './viewOverrides.vue';
import BackButton from '../../../../components/widgets/BackButton.vue';
import ViewAssignedStaff from './viewAssignedStaff.vue';
import CreateBranchModal from './createBranchModal.vue';
import ViewStaffUnavailability from './viewStaffUnavailability.vue';
import DeleteConfirmationModal from './deleteConfirmationModal.vue';
import ViewStaffSchedule from './viewStaffSchedule.vue';

export default{
  components:{
    LoadingState,
    CreateServiceModal,
    CreateStaffModal,
    CreateScheduleModal,
    ViewWorkingHours,
    ToggleScheduleButton,
    ViewStaffServices,
    SettingIntroBanner,
    ViewOverrides,
    BackButton,
    ViewAssignedStaff,
    CreateBranchModal,
    ViewStaffUnavailability,
    DeleteConfirmationModal,
    ViewStaffSchedule,
  },
  props: {
    handleBack: {
      type: Function,
      default: () => {},
    }
  },
  computed: {
    ...mapGetters('appointments', ['selectedBranch', 'serviceList', 'staffList', 'scheduleList']),
    branchId() {
      return this.$route.params.branchId;
    },
    serviceLoading() {
      return this.$store.state.appointments.serviceLoading || false;
    },
    serviceCurrentPage() { 
      return this.$store.state.appointments.servicePagination.page 
    },
    selectedTabKey() {
      return this.tabs[this.selectedTabIndex]?.key;
    },
    selectedSchedule(){
      if(this.selectedScheduleId){
        return this.scheduleList.find((schedule) => schedule.schedule_id === this.selectedScheduleId)
      }else{
        return null;
      }
    },
    tabs() {
      return [
        {
          key: 'general',
          name: this.$t('BRANCHES.VIEW_BRANCH.TABS.GENERAL'),
        },
        {
          key: 'availability',
          name: this.$t('BRANCHES.VIEW_BRANCH.TABS.AVAILABILITY'),
        },
        {
          key: 'services',
          name: this.$t('BRANCHES.VIEW_BRANCH.TABS.SERVICES'),
        },
        {
          key: 'staff',
          name: this.$t('BRANCHES.VIEW_BRANCH.TABS.STAFF'),
        }
      ];
    },
    availableStaff() {
      return this.staffList.length ? this.staffList : this.STAFF_LIST_EXAMPLE;
    },
    availableServices() {
      return this.serviceList.length ? this.serviceList : this.SERVICE_LIST_EXAMPLE;
    },
  },
  data(){
    return {
      isLoading: false,
      isCreatingService: false,
      isEditingService: false,
      editServiceId: null,
      isCreatingStaff: false,
      isEditingStaff: false,
      editStaffId: null,
      isCreatingSchedule: false,
      isEditingSchedule: false,
      editScheduleId: null,
      selectedTabIndex: 0,
      selectedScheduleId: null,
      isEditingBranch: false,
      deleteStaffId: null,
      deleteScheduleId: null,
      deleteServiceObject: null,
      deleteStaffObject: null,
      deleteScheduleObject: null,
      servicePageSize: '10',
    }
  },
  watch: {
    servicePageSize(newVal) {
      this.$store.dispatch("appointments/changeServicePageSize", { branchId: this.branchId, size: newVal });
    }
  },
  methods: {
    ...mapActions('appointments', [
      'fetchBranch', 
      'fetchServicesV2', 
      'deleteService', 
      'fetchStaffList', 
      'deleteStaff', 
      'fetchScheduleList', 
      'deleteSchedule',
      'fetchWorkingHours'
    ]),
    resetCreateService() {
      this.isCreatingService = false;
      this.isEditingService = false;
      this.editServiceId = null;
    },
    resetCreateStaff(){
      this.isCreatingStaff = false;
      this.isEditingStaff = false;
      this.editStaffId = null;
    },
    resetCreateSchedule(){
      this.isCreatingSchedule = false;
      this.isEditingSchedule = false;
      this.editScheduleId = null;
      if(this.scheduleList && this.scheduleList.length > 0){
        this.selectedScheduleId = this.scheduleList[0].schedule_id
      }
    },
    handleEditService(serviceId){
      this.isEditingService = true;
      this.editServiceId = serviceId;
    },
    handleEditStaff(staffId){
      this.isEditingStaff = true;
      this.editStaffId = staffId;
    },
    handleEditSchedule(scheduleId){
      this.isEditingSchedule = true;
      this.editScheduleId = scheduleId;
    },
    async handleDeleteService(serviceId){
      try{
        await this.deleteService(serviceId);
        await this.fetchServicesV2(this.branchId);
      } catch(err){
        console.error(err);
        useAlert(err);
      } finally{
        this.deleteServiceObject = null;
      }
    },
    async handleDeleteStaff(staffId){
      try{
        await this.deleteStaff(staffId);
        this.fetchStaffList(this.branchId);
      } catch(err){
        useAlert(err);
      } finally{
        this.deleteStaffObject = null;
      }
    },
    async handleDeleteSchedule(scheduleId){
      try{
        await this.deleteSchedule(scheduleId);
        await this.fetchScheduleList(this.branchId);
      } catch(err){
        console.error(err);
        useAlert(err);
      } finally{
        if(this.scheduleList && this.scheduleList.length > 0){
          this.selectedScheduleId = this.scheduleList[0].schedule_id;
          await this.fetchWorkingHours(this.selectedScheduleId);
        }
        this.deleteScheduleObject = null;
      }
    },
    onTabChange(selectedTabIndex) {
      this.selectedTabIndex = selectedTabIndex;
    },
    async resetCreateBranch(){
      this.isEditingBranch = false;
      await this.fetchBranch(this.branchId);
    },
    async refreshSelectedSchedule(){
      await this.fetchScheduleList(this.branchId);
    },
    handleServiceNext() {
      this.$store.dispatch("appointments/nextServicePage", this.branchId)
    },
    handleServicePrevious() {
      this.$store.dispatch("appointments/prevServicePage", this.branchId)
    }
  },
  async mounted(){
    try{
      this.isLoading = true;
      await this.fetchBranch(this.branchId);
      await this.$store.dispatch("appointments/changeServicePageSize", { branchId: this.branchId, size: this.servicePageSize });
      // await this.fetchServicesV2(this.branchId);
      await this.fetchStaffList(this.branchId);
      await this.fetchScheduleList(this.branchId);
      
      if(this.scheduleList && this.scheduleList.length > 0){
        this.selectedScheduleId = this.scheduleList[0].schedule_id
      }
    } catch(err){
      console.error(err);
      useAlert(err);
    } finally{
      this.isLoading = false;
    }
  },
}
</script>

<template>
  <div class="flex-1 overflow-auto">
    <div
      class="flex justify-between items-center h-14 min-h-[3.5rem] px-4 py-2 bg-white dark:bg-slate-900 border-b border-slate-50 dark:border-slate-800/50"
    >
      <h1
        class="flex items-center mb-0 text-2xl text-slate-900 dark:text-slate-100"
      >
        <BackButton
          :button-label="$t('BRANCHES.VIEW_BRANCH.BACK')"
          back-url=""
          class="ml-2 mr-4"
        />
        <fluent-icon
          icon="building-bank"
          class="hidden ml-1 mr-2 rtl:ml-2 rtl:mr-1 md:block"
        />
        <span class="text-2xl font-medium text-slate-900 dark:text-slate-100">
          {{ $t('BRANCHES.VIEW_BRANCH.BRANCH') }}
        </span>
      </h1>
    </div>
    <SettingIntroBanner
      :header-title="selectedBranch.branch_name"
      class="mb-8"
    >
      <woot-tabs
        class="settings--tabs"
        :index="selectedTabIndex"
        :border="false"
        @change="onTabChange"
      >
        <woot-tabs-item
          v-for="tab in tabs"
          :key="tab.key"
          :name="tab.name"
          :show-badge="false"
        />
      </woot-tabs>
    </SettingIntroBanner>

    <div 
      v-if="isCreatingService || isEditingService" 
      class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
    >
      <CreateServiceModal
        :isEditingService="isEditingService" 
        :branchId="branchId" 
        :serviceId="editServiceId" 
        :resetCreateService="resetCreateService"
      />
    </div>

    <div 
      v-if="isCreatingStaff || isEditingStaff" 
      class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
    >
      <CreateStaffModal
        :isEditingStaff="isEditingStaff" 
        :branchId="branchId" 
        :editStaffId="editStaffId" 
        :resetCreateStaff="resetCreateStaff"
      />
    </div>

    <div 
      v-if="isCreatingSchedule || isEditingSchedule" 
      class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
    >
      <CreateScheduleModal 
        :isEditingSchedule="isEditingSchedule" 
        :branchId="branchId" 
        :editScheduleId="editScheduleId" 
        :resetCreateSchedule="resetCreateSchedule"
        @created="(newScheduleId) => selectedScheduleId = newScheduleId"
      />
    </div>

    <div 
      v-if="isEditingBranch" 
      class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
    >
      <CreateBranchModal
        :isEditingBranch="isEditingBranch" 
        :branchId="branchId" 
        :resetCreateBranch="resetCreateBranch"
      />
    </div>

    <div 
      v-if="deleteServiceObject" 
      class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
    >
      <DeleteConfirmationModal
        :deleteName="deleteServiceObject.name"
        @confirm-delete="handleDeleteService(deleteServiceObject.id)"
        @close="deleteServiceObject = null"
      />
    </div>

    <div 
      v-if="deleteStaffObject" 
      class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
    >
      <DeleteConfirmationModal
        :deleteName="deleteStaffObject.name"
        @confirm-delete="handleDeleteStaff(deleteStaffObject.id)"
        @close="deleteStaffObject = null"
      />
    </div>

    <div 
      v-if="deleteScheduleObject" 
      class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
    >
      <DeleteConfirmationModal
        :deleteName="deleteScheduleObject.name"
        @confirm-delete="handleDeleteSchedule(deleteScheduleObject.id)"
        @close="deleteScheduleObject = null"
      />
    </div>

    <div v-if="!isLoading" class="px-8">
      <div v-if="selectedTabKey === 'general'" class="shadow-md border rounded-lg p-6 space-y-6">
        <!-- Header -->
        <div class="flex justify-between items-center border-b pb-4">
          <h1 class="text-xl font-semibold text-slate-900 dark:text-white">
            {{ $t('BRANCHES.VIEW_BRANCH.GENERAL') }}
          </h1>
          <woot-button
            variant="smooth"
            color-scheme="primary"
            icon="edit"
            @click="isEditingBranch = true"
          >
            {{ $t('BRANCHES.VIEW_BRANCH.EDIT_BRANCH') }}
          </woot-button>
        </div>

        <!-- Branch Details -->
        <div class="space-y-4">
          <div>
            <p class="text-slate-500 text-sm">{{ $t('BRANCHES.VIEW_BRANCH.BRANCH_NAME') }}</p>
            <p class="text-lg font-medium text-slate-900 dark:text-white">
              {{ selectedBranch.branch_name }}
            </p>
          </div>
          <div>
            <p class="text-slate-500 text-sm">{{ $t('BRANCHES.VIEW_BRANCH.PHONE_NUMBER') }}</p>
            <p class="text-lg font-medium text-slate-900 dark:text-white">
              {{ selectedBranch.phone_number }}
            </p>
          </div>
          <div>
            <p class="text-slate-500 text-sm">{{ $t('BRANCHES.VIEW_BRANCH.ADDRESS') }}</p>
            <p class="text-lg font-medium text-slate-900 dark:text-white">
              {{ selectedBranch.address }}
            </p>
          </div>
        </div>

        <!-- Staff List -->
        <div class="space-y-2">
          <p class="text-slate-500 text-sm font-medium">Assigned Staff</p>
          <!-- <div class="flex flex-wrap gap-2">
            <span
              v-for="staff in selectedStaff"
              :key="staff.id"
              class="group bg-slate-200 dark:bg-slate-600 text-slate-800 dark:text-white px-3 py-1 rounded-md text-sm flex items-center gap-1 relative"
            >
              {{ staff.name }}
              <span
                class="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-slate-500 italic"
              >
                (Go to details)
              </span>
            </span>
            <span v-if="selectedStaff.length === 0" class="text-slate-400">No staff assigned</span>
          </div> -->

          <div>
            <multiselect
              v-model="selectedStaff"
              :options="availableStaff"
              label="name"
              track-by="id"
              placeholder="Select staff..."
              multiple
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
            />
          </div>
        </div>

        <!-- Service List -->
        <div class="space-y-2">
          <p class="text-slate-500 text-sm font-medium">Assigned Services</p>
          <!-- <div class="flex flex-wrap gap-2">
            <span
              v-for="service in selectedServices"
              :key="service.id"
              class="bg-slate-200 dark:bg-slate-600 text-slate-800 dark:text-white px-3 py-1 rounded-md text-sm"
            >
              {{ service.serviceName }}
            </span>
            <span v-if="selectedServices.length === 0" class="text-slate-400">No services assigned</span>
          </div> -->

          <div>
            <multiselect
              v-model="selectedServices"
              :options="availableServices"
              label="serviceName"
              track-by="id"
              placeholder="Select services..."
              multiple
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
            />
          </div>
        </div>

        <!-- Save button -->
        <div v-if="isEditingBranch" class="flex justify-end pt-4 border-t">
          <woot-button color-scheme="primary" @click="saveBranchAssociations">
            Save Changes
          </woot-button>
        </div>
      </div>

      <!-- Branch Schedules List -->
      <div v-if="selectedTabKey === 'availability'" class="flex flex-col gap-4 mb-8">
        <div class="flex justify-between items-center">
          <h1 class="text-xl font-semibold text-slate-900 dark:text-white">
            {{ $t('BRANCHES.VIEW_BRANCH.SCHEDULES') }}
          </h1>
          <woot-button
            variant="smooth"
            color-scheme="primary"
            icon="plus-sign"
            @click="isCreatingSchedule = true"
          >
            {{ $t('BRANCHES.VIEW_BRANCH.CREATE_SCHEDULE') }}
          </woot-button>
        </div>

        <div class="flex gap-4 overflow-auto">
          <button 
            v-for="(schedule, index) in scheduleList" 
            :key="index" 
            @click="selectedScheduleId = schedule.schedule_id"
            class="px-5 py-3 border-2 border-slate-500 rounded-lg cursor-pointer relative group my-4"
            :class="{'bg-woot-500 text-white': selectedScheduleId === schedule.schedule_id}"
          >
            {{ schedule.name }}
            <span 
              class="absolute right-0 top-0 translate-x-1/2 -translate-y-1/2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              @click.stop="deleteScheduleObject = { id: schedule.schedule_id, name: schedule.name }"
            >
              <fluent-icon icon="dismiss-circle" />
            </span>
          </button>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6" v-if="scheduleList.length > 0">
          <div v-if="!selectedSchedule" class="text-center text-slate-500 dark:text-slate-300 py-6 bg-slate-100 dark:bg-slate-800 rounded-md shadow-sm">
            <p>{{ $t('BRANCHES.VIEW_BRANCH.NO_SCHEDULE_SELECTED') }}</p>
          </div>

          <ViewWorkingHours 
            v-if="selectedSchedule" 
            :scheduleId="selectedScheduleId" 
            :selectedSchedule="selectedSchedule" 
            @refreshSelectedSchedule="refreshSelectedSchedule"
          />

          <div class="flex flex-col gap-6">
            <ViewAssignedStaff :scheduleId="selectedScheduleId" :branchId="branchId" />
            <ViewOverrides :scheduleId="selectedScheduleId"/>
          </div>
        </div>
        <div v-if="scheduleList.length === 0" class="text-center text-slate-500 dark:text-slate-300 py-6 bg-slate-100 dark:bg-slate-800 rounded-md shadow-sm">
            <p>{{ $t('BRANCHES.VIEW_BRANCH.NO_SCHEDULE_ADDED') }}</p>
          </div>
        </div>

        <!-- Branch Staff List -->
        <div v-if="selectedTabKey === 'staff'" class="flex flex-col gap-4 mb-8">
          <div class="flex justify-between items-center">
            <h1 class="text-xl font-semibold text-slate-900 dark:text-white">
              {{ $t('BRANCHES.VIEW_BRANCH.STAFF_LIST') }}
            </h1>
            <woot-button
              variant="smooth"
              color-scheme="primary"
              icon="plus-sign"
              @click="isCreatingStaff = true"
            >
              {{ $t('BRANCHES.VIEW_BRANCH.CREATE_STAFF') }}
            </woot-button>
          </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div 
            v-for="staff in staffList.results" 
            :key="staff.staff_id" 
            class="p-6 bg-white dark:bg-slate-800 shadow-lg rounded-xl border border-slate-200 dark:border-slate-700"
          >
            <div class="flex justify-between items-center">
              <div>
                <h1 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {{ staff.staff_name }}
                </h1>
                <div class="mt-1 flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-300">
                  <p><span class="font-medium">{{ $t('BRANCHES.VIEW_BRANCH.GENDER') }}:</span> {{ staff.gender || '---' }}</p>
                  <p><span class="font-medium">{{ $t('BRANCHES.VIEW_BRANCH.SENIORITY') }}:</span> {{ staff.seniority || '---' }}</p>
                </div>
              </div>

              <div class="flex gap-2">
                <button
                  @click="handleEditStaff(staff.staff_id)"
                  class="px-4 py-1.5 text-sm font-medium text-woot-600 border border-woot-600 rounded-lg 
                    hover:bg-woot-600 hover:text-white transition duration-200"
                >
                  {{ $t('BRANCHES.VIEW_BRANCH.EDIT') }}
                </button>
                <button 
                  @click="deleteStaffObject = { id: staff.staff_id, name: staff.staff_name }"
                  class="px-4 py-1.5 text-sm font-medium text-red-600 border border-red-600 rounded-lg 
                    hover:bg-red-600 hover:text-white transition duration-200"
                >
                  {{ $t('BRANCHES.VIEW_BRANCH.DELETE') }}
                </button>
              </div>
            </div>

            <div class="mt-4 space-y-3">
              <ViewStaffServices :staffId="staff.staff_id" :branchId="branchId" />
              <ViewStaffUnavailability :staffId="staff.staff_id" />
              <ViewStaffSchedule :staffId="staff.staff_id" :staffList="staffList" :scheduleList="scheduleList" />
            </div>
          </div>
          
          <div v-if="staffList.results.length === 0" class="col-span-full text-center py-10">
            <div class="flex flex-col items-center gap-3 text-slate-500 dark:text-slate-400">
              <p>{{ $t('BRANCHES.VIEW_BRANCH.NO_STAFF_ADDED') }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Branch Service List -->
      <div v-if="selectedTabKey === 'services'" class="flex flex-col gap-4 mb-8">
        <div class="flex justify-between items-center">
          <h1 class="text-xl font-semibold text-slate-900 dark:text-white">
            {{ $t('BRANCHES.VIEW_BRANCH.SERVICES') }}
          </h1>
          <woot-button
            variant="smooth"
            color-scheme="primary"
            icon="plus-sign"
            @click="isCreatingService = true"
          >
            {{ $t('BRANCHES.VIEW_BRANCH.CREATE_SERVICE') }}
          </woot-button>
        </div>

        <!-- Pagination -->
        <div class="flex justify-between gap-4">
          <!-- Page size control -->
          <select v-model="servicePageSize" class="w-36">
            <option value="5">5 per page</option>
            <option value="10">10 per page</option>
            <option value="15">15 per page</option>
            <option value="20">20 per page</option>
          </select>

          <!-- Page control -->
          <div class="flex gap-4">
            <woot-button color-scheme="secondary" @click="handleServicePrevious">
              <fluent-icon icon="chevron-left" />
              Prev
            </woot-button>
            <woot-button>{{ serviceCurrentPage }}</woot-button>
            <woot-button color-scheme="secondary" @click="handleServiceNext">
              Next
              <fluent-icon icon="chevron-right" />
            </woot-button>
          </div>
        </div>

        <LoadingState v-if="serviceLoading" message="Loading services"></LoadingState>

        <div v-else-if="!serviceLoading && serviceList.results.length > 0" class="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div v-for="service in serviceList.results" :key="service.service_id" class="flex flex-col bg-white dark:bg-slate-700 rounded-lg shadow-md overflow-hidden">
            <div class="relative h-48 w-full bg-slate-100 dark:bg-slate-600">
              <img 
                v-if="service.presigned_image_url" 
                :src="service.presigned_image_url" 
                class="w-full h-full object-cover"
                alt="Service image"
              />
              <div v-else class="w-full h-full flex items-center justify-center text-slate-400 dark:text-slate-500">
                <!-- TODO: i18n this alt? -->
                <svg class="w-16 h-16" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"></path>
                </svg>
              </div>
            </div>

            <div class="p-4 flex-grow">
              <h3 class="text-lg font-semibold text-slate-800 dark:text-white mb-2">{{ service.service_name }}</h3>
              <div class="flex flex-wrap gap-2 mb-3">
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-woot-100 text-woot-800 dark:bg-woot-900 dark:text-woot-200">
                  {{ Math.round(service.duration * 60) }} {{ Math.round(service.duration * 60) > 1 ? $t('BRANCHES.VIEW_BRANCH.MINUTES') : $t('BRANCHES.VIEW_BRANCH.MINUTE') }}
                </span>
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                  {{ service.max_serve }} {{ service.max_serve > 1 ? $t('BRANCHES.VIEW_BRANCH.CLIENTS') : $t('BRANCHES.VIEW_BRANCH.CLIENT') }}
                </span>
              </div>

              <p v-if="service.description" class="text-sm text-slate-600 dark:text-slate-300 mb-4 line-clamp-2">
                {{ service.description }}
              </p>

              <p v-if="service.price" class="text-sm text-slate-600 dark:text-slate-300 mb-4 line-clamp-2">
                {{ service.price }}
              </p>
            </div>

            <div class="p-3 border-t border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 flex justify-end gap-2">
              <button 
                @click="handleEditService(service.service_id)"
                class="px-3 py-1 text-sm font-medium text-woot-600 border border-woot-600 rounded-md hover:bg-woot-600 hover:text-white transition"
              >
                {{ $t('BRANCHES.VIEW_BRANCH.EDIT') }}
              </button>
              <button 
                @click="deleteServiceObject = { id: service.service_id, name: service.service_name }"
                class="px-3 py-1 text-sm font-medium text-red-600 border border-red-600 rounded-md hover:bg-red-600 hover:text-white transition"
              >
                 {{ $t('BRANCHES.VIEW_BRANCH.DELETE') }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="!serviceLoading && serviceList.results.length === 0" class="text-center text-slate-500 dark:text-slate-400">
          <p>{{ $t('BRANCHES.VIEW_BRANCH.NO_SERVICES_ADDED') }}</p>
        </div>
      </div>
    </div>
    <LoadingState v-else />
  </div>
</template>

<style scoped lang="scss">
.settings--tabs {
  ::v-deep .tabs {
    @apply p-0;
  }
}
</style>